import { Link } from 'react-router-dom';
import type { EgressFirewallRef, FirewallRulesetSummary } from '@nadoutong/contracts';
import { useT } from '../i18n/index.ts';
import { FormField, Select } from '../ui/index.ts';

/**
 * ① Firewall ruleset reference, inside the egress editor's inbound area
 * (#1760).
 *
 * Presentational on purpose. It takes the ruleset list rather than fetching it,
 * for the same reason `EgressAdmissionBlacklistFields` takes its value: there is
 * more than one host, and a component that fetched would force a query client
 * into every render path that mounts an egress editor.
 *
 * The one thing this control exists to make unmissable is that its "off" is not
 * the neighbouring block's "off". No ruleset referenced means the layer is
 * SKIPPED — the request goes straight to ② — whereas admission left unticked
 * still runs as `allow-any-supported` and still refuses unrecognized clients.
 * Two switches in one panel whose off states disagree is where operators get
 * hurt, so the copy nails both rather than leaving it to be inferred.
 */

/** Sentinel for "no ruleset" in the Select, which carries string values only. */
const NO_RULESET = '';
/** Sentinel for "follow whatever `active_version` points at". */
const FOLLOW_ACTIVE = 'active';

export type FirewallArmingScope = 'profile' | 'binding';

function versionOptionValues(latest: number | null): readonly number[] {
  if (latest === null) return [];
  return Array.from({ length: latest }, (_, index) => latest - index);
}

export function EgressFirewallFields({
  value,
  rulesets,
  rulesetsUnavailable = false,
  scope,
  disabled,
  idPrefix,
  onChange,
}: {
  /** `null` = no reference at all, which is the skip-this-layer state. */
  readonly value: EgressFirewallRef | null;
  readonly rulesets: readonly FirewallRulesetSummary[];
  /** True when the list could not be read; the current reference is kept. */
  readonly rulesetsUnavailable?: boolean;
  readonly scope: FirewallArmingScope;
  readonly disabled: boolean;
  readonly idPrefix: string;
  readonly onChange: (next: EgressFirewallRef | null) => void;
}): JSX.Element {
  const t = useT();
  const selected = rulesets.find((ruleset) => ruleset.id === value?.rulesetId) ?? null;
  const referenced = value !== null;

  // A stored reference whose ruleset is not in the list still has to render as
  // itself: it is live policy. Showing it as "not referenced" would invite an
  // operator to "fix" it by picking something else and silently repoint a
  // binding that was working.
  const options = [
    { value: NO_RULESET, label: t('egress_editor.firewall_ruleset_none') },
    ...rulesets.map((ruleset) => ({ value: ruleset.id, label: ruleset.name })),
    ...(referenced && selected === null && value !== null
      ? [{ value: value.rulesetId, label: value.rulesetId }]
      : []),
  ];

  const versionValue = value?.version === undefined ? FOLLOW_ACTIVE : String(value.version);
  const activeLabel = selected?.active_version === null || selected?.active_version === undefined
    ? t('egress_editor.firewall_version_follow_unarmed')
    : t('egress_editor.firewall_version_follow', { version: selected.active_version });

  return (
    <div
      className="flex flex-col gap-3 rounded-lg bg-card p-3"
      data-testid={`${idPrefix}-egress-firewall`}
    >
      <div className="flex flex-col gap-0.5">
        <h4 className="m-0 text-label font-medium text-paper-mid">
          {t('egress_editor.firewall')}
        </h4>
        <p className="m-0 text-label text-paper-low">{t('egress_editor.firewall_hint')}</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <FormField
          label={t('egress_editor.firewall_ruleset')}
          fieldId={`${idPrefix}-egress-firewall-ruleset`}
          hint={rulesetsUnavailable ? t('egress_editor.firewall_list_unavailable') : undefined}
        >
          <Select
            id={`${idPrefix}-egress-firewall-ruleset`}
            value={value?.rulesetId ?? NO_RULESET}
            onValueChange={(next) => {
              onChange(next === NO_RULESET ? null : { rulesetId: next });
            }}
            disabled={disabled}
            testId={`${idPrefix}-egress-firewall-ruleset`}
            options={options}
          />
        </FormField>

        {referenced ? (
          <FormField
            label={t('egress_editor.firewall_version')}
            fieldId={`${idPrefix}-egress-firewall-version`}
            hint={t('egress_editor.firewall_version_hint')}
          >
            <Select
              id={`${idPrefix}-egress-firewall-version`}
              value={versionValue}
              onValueChange={(next) => {
                if (value === null) return;
                if (next === FOLLOW_ACTIVE) {
                  onChange({ rulesetId: value.rulesetId });
                  return;
                }
                const parsed = Number.parseInt(next, 10);
                if (Number.isInteger(parsed) && parsed > 0) {
                  onChange({ rulesetId: value.rulesetId, version: parsed });
                }
              }}
              disabled={disabled}
              testId={`${idPrefix}-egress-firewall-version`}
              options={[
                { value: FOLLOW_ACTIVE, label: activeLabel },
                ...versionOptionValues(selected?.latest_version ?? null).map((version) => ({
                  value: String(version),
                  label: t('egress_editor.firewall_version_pinned', { version }),
                })),
                // A pinned version that is no longer in the list (the ruleset
                // was not readable, or the pin predates it) still shows as the
                // selected option rather than snapping to "follow active",
                // which would be a silent policy change on open.
                ...(value?.version !== undefined
                  && !versionOptionValues(selected?.latest_version ?? null).includes(value.version)
                  ? [{
                    value: String(value.version),
                    label: t('egress_editor.firewall_version_pinned', { version: value.version }),
                  }]
                  : []),
              ]}
            />
          </FormField>
        ) : null}
      </div>

      {referenced ? null : (
        <p
          className="m-0 rounded bg-well p-2 text-label text-paper-mid"
          data-testid={`${idPrefix}-egress-firewall-skip-note`}
        >
          {t('egress_editor.firewall_skip_note')}
        </p>
      )}

      {referenced && scope === 'profile' ? (
        // Same blast radius as the blacklist warning next door, and stated for
        // the same reason: one profile backs many bindings, and a ruleset that
        // is right for one namespace can refuse another namespace's correct
        // traffic.
        <p
          className="m-0 rounded bg-warn-dim/40 p-2 text-label text-warn"
          data-testid={`${idPrefix}-egress-firewall-profile-scope-warning`}
        >
          {t('egress_editor.firewall_profile_scope_warning')}
        </p>
      ) : null}

      <Link
        to="/supply/firewall-rulesets"
        className="text-label text-signal underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
        data-testid={`${idPrefix}-egress-firewall-manage-link`}
      >
        {t('egress_editor.firewall_manage_link')}
      </Link>
    </div>
  );
}
